import { useState } from 'react';
import { X, Package, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react';
import type { DeployConfigLotto1 } from '@onecreator/shared';
import { deployConfigSchemaLotto1 } from '@onecreator/shared';

interface DeployConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (config?: DeployConfigLotto1) => void;
  projectName: string;
}

type DeployFormState = Partial<DeployConfigLotto1>;

interface ValidationError {
  path: string;
  message: string;
}

export function DeployConfigModal({
  isOpen,
  onClose,
  onConfirm,
  projectName,
}: DeployConfigModalProps) {
  const [formState, setFormState] = useState<DeployFormState>({});
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    ambiente: true,
    docker: false,
    security: false,
    nginx: false,
    backup: false,
  });

  if (!isOpen) return null;

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const updateField = (path: string, value: unknown) => {
    setFormState((prev) => {
      const newState = { ...prev };
      const parts = path.split('.');

      if (parts.length === 1) {
        (newState as any)[parts[0]] = value;
      } else if (parts.length === 2) {
        (newState as any)[parts[0]] = { ...(newState as any)[parts[0]], [parts[1]]: value };
      } else if (parts.length === 3) {
        // deploy.docker.nginx.memoryLimit
        const [cat, sub, field] = parts;
        (newState as any)[cat] = {
          ...(newState as any)[cat],
          [sub]: { ...((newState as any)[cat]?.[sub] || {}), [field]: value },
        };
      }

      return newState;
    });
    // Clear errors on change
    setErrors([]);
  };

  const handleSubmit = () => {
    // Validate with Zod
    const result = deployConfigSchemaLotto1.safeParse(formState);

    if (!result.success) {
      const validationErrors: ValidationError[] = result.error.issues.map((issue) => ({
        path: issue.path.join('.'),
        message: issue.message,
      }));
      setErrors(validationErrors);
      return;
    }

    // If form is empty, pass undefined (use global settings only)
    const hasOverrides = Object.keys(formState).length > 0;
    onConfirm(hasOverrides ? formState : undefined);
    onClose();
  };

  const handleReset = () => {
    setFormState({});
    setErrors([]);
  };

  const getFieldError = (path: string): string | undefined => {
    return errors.find((e) => e.path === path)?.message;
  };

  // Conditional rendering helpers
  const backupEnabled = formState.backup?.enabled !== false; // default true
  const includeUploads = formState.backup?.includeUploads !== false; // default true

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative z-10 w-full max-w-3xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <Package className="w-5 h-5 text-blue-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Configura Deploy
                </h2>
                <p className="text-sm text-gray-500">
                  {projectName}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
            <p className="text-sm text-gray-600 mb-4">
              Personalizza i parametri di deploy per questo pacchetto. I campi non compilati useranno i valori
              configurati nelle Settings globali.
            </p>

            {/* Global validation errors */}
            {errors.length > 0 && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-red-700">
                    <p className="font-medium mb-1">Errori di validazione:</p>
                    <ul className="list-disc list-inside space-y-1">
                      {errors.map((err, idx) => (
                        <li key={idx}>
                          <span className="font-mono text-xs">{err.path}</span>: {err.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Ambiente */}
            <AccordionSection
              title="Ambiente"
              isExpanded={expandedSections.ambiente}
              onToggle={() => toggleSection('ambiente')}
            >
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Ambiente"
                  error={getFieldError('environment')}
                >
                  <select
                    value={formState.environment || ''}
                    onChange={(e) => updateField('environment', e.target.value || undefined)}
                    className="input"
                  >
                    <option value="">-- Default (production) --</option>
                    <option value="production">Production</option>
                    <option value="staging">Staging</option>
                    <option value="development">Development</option>
                  </select>
                </FormField>

                <FormField
                  label="Porta Web"
                  error={getFieldError('webPort')}
                >
                  <input
                    type="number"
                    value={formState.webPort || ''}
                    onChange={(e) => updateField('webPort', e.target.value ? Number(e.target.value) : undefined)}
                    placeholder="80"
                    min={1}
                    max={65535}
                    className="input"
                  />
                </FormField>

                <FormField
                  label="URL Base"
                  error={getFieldError('baseUrl')}
                  className="col-span-2"
                >
                  <input
                    type="text"
                    value={formState.baseUrl || ''}
                    onChange={(e) => updateField('baseUrl', e.target.value || undefined)}
                    placeholder="https://www.example.com"
                    className="input"
                  />
                </FormField>

                <FormField
                  label="Timezone"
                  error={getFieldError('timezone')}
                  className="col-span-2"
                >
                  <input
                    type="text"
                    value={formState.timezone || ''}
                    onChange={(e) => updateField('timezone', e.target.value || undefined)}
                    placeholder="Europe/Rome"
                    className="input"
                  />
                </FormField>
              </div>
            </AccordionSection>

            {/* Docker */}
            <AccordionSection
              title="Docker"
              isExpanded={expandedSections.docker}
              onToggle={() => toggleSection('docker')}
            >
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Memoria Nginx"
                  error={getFieldError('docker.nginx.memoryLimit')}
                  hint="Formato: 128M, 1G"
                >
                  <input
                    type="text"
                    value={formState.docker?.nginx?.memoryLimit || ''}
                    onChange={(e) => updateField('docker.nginx.memoryLimit', e.target.value || undefined)}
                    placeholder="128M"
                    className="input"
                  />
                </FormField>

                <FormField
                  label="Memoria Backend"
                  error={getFieldError('docker.backend.memoryLimit')}
                  hint="Formato: 512M, 2G"
                >
                  <input
                    type="text"
                    value={formState.docker?.backend?.memoryLimit || ''}
                    onChange={(e) => updateField('docker.backend.memoryLimit', e.target.value || undefined)}
                    placeholder="512M"
                    className="input"
                  />
                </FormField>

                <FormField
                  label="Dimensione Max Log"
                  error={getFieldError('docker.logMaxSize')}
                  hint="Formato: 10M, 50M"
                >
                  <input
                    type="text"
                    value={formState.docker?.logMaxSize || ''}
                    onChange={(e) => updateField('docker.logMaxSize', e.target.value || undefined)}
                    placeholder="10M"
                    className="input"
                  />
                </FormField>

                <FormField
                  label="Max File di Log"
                  error={getFieldError('docker.logMaxFiles')}
                >
                  <input
                    type="number"
                    value={formState.docker?.logMaxFiles || ''}
                    onChange={(e) => updateField('docker.logMaxFiles', e.target.value ? Number(e.target.value) : undefined)}
                    placeholder="5"
                    min={1}
                    max={100}
                    className="input"
                  />
                </FormField>
              </div>
            </AccordionSection>

            {/* Security */}
            <AccordionSection
              title="Security"
              isExpanded={expandedSections.security}
              onToggle={() => toggleSection('security')}
            >
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Rate Limit API"
                  error={getFieldError('security.rateLimitApi')}
                  hint="Formato: 30r/s (30 req/sec)"
                >
                  <input
                    type="text"
                    value={formState.security?.rateLimitApi || ''}
                    onChange={(e) => updateField('security.rateLimitApi', e.target.value || undefined)}
                    placeholder="30r/s"
                    className="input"
                  />
                </FormField>

                <FormField
                  label="Rate Limit Deploy"
                  error={getFieldError('security.rateLimitDeploy')}
                  hint="Formato: 2r/m (2 req/min)"
                >
                  <input
                    type="text"
                    value={formState.security?.rateLimitDeploy || ''}
                    onChange={(e) => updateField('security.rateLimitDeploy', e.target.value || undefined)}
                    placeholder="2r/m"
                    className="input"
                  />
                </FormField>

                <FormField
                  label="Max Dimensione Upload"
                  error={getFieldError('security.clientMaxBodySize')}
                  hint="Formato: 5M, 10M"
                  className="col-span-2"
                >
                  <input
                    type="text"
                    value={formState.security?.clientMaxBodySize || ''}
                    onChange={(e) => updateField('security.clientMaxBodySize', e.target.value || undefined)}
                    placeholder="5M"
                    className="input"
                  />
                </FormField>
              </div>
            </AccordionSection>

            {/* Nginx */}
            <AccordionSection
              title="Nginx"
              isExpanded={expandedSections.nginx}
              onToggle={() => toggleSection('nginx')}
            >
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Livello Compressione Gzip"
                  error={getFieldError('nginx.gzipCompressionLevel')}
                  hint="1-9 (1=veloce, 9=massima compressione)"
                >
                  <input
                    type="number"
                    value={formState.nginx?.gzipCompressionLevel || ''}
                    onChange={(e) => updateField('nginx.gzipCompressionLevel', e.target.value ? Number(e.target.value) : undefined)}
                    placeholder="6"
                    min={1}
                    max={9}
                    className="input"
                  />
                </FormField>

                <FormField
                  label="Timeout Connessione Proxy"
                  error={getFieldError('nginx.proxyConnectTimeout')}
                  hint="Formato: 10s, 30s"
                >
                  <input
                    type="text"
                    value={formState.nginx?.proxyConnectTimeout || ''}
                    onChange={(e) => updateField('nginx.proxyConnectTimeout', e.target.value || undefined)}
                    placeholder="10s"
                    className="input"
                  />
                </FormField>

                <FormField
                  label="Timeout Lettura Proxy"
                  error={getFieldError('nginx.proxyReadTimeout')}
                  hint="Formato: 30s, 60s"
                  className="col-span-2"
                >
                  <input
                    type="text"
                    value={formState.nginx?.proxyReadTimeout || ''}
                    onChange={(e) => updateField('nginx.proxyReadTimeout', e.target.value || undefined)}
                    placeholder="30s"
                    className="input"
                  />
                </FormField>
              </div>
            </AccordionSection>

            {/* Backup */}
            <AccordionSection
              title="Backup"
              isExpanded={expandedSections.backup}
              onToggle={() => toggleSection('backup')}
            >
              <div className="space-y-4">
                <FormField
                  label="Backup Automatico"
                  error={getFieldError('backup.enabled')}
                >
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formState.backup?.enabled !== false}
                      onChange={(e) => updateField('backup.enabled', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Abilita backup automatico</span>
                  </label>
                </FormField>

                {backupEnabled && (
                  <>
                    <FormField
                      label="Schedule (Cron)"
                      error={getFieldError('backup.schedule')}
                      hint="Es: 0 2 * * * = ore 02:00 ogni giorno"
                    >
                      <input
                        type="text"
                        value={formState.backup?.schedule || ''}
                        onChange={(e) => updateField('backup.schedule', e.target.value || undefined)}
                        placeholder="0 2 * * *"
                        className="input"
                      />
                    </FormField>

                    <FormField
                      label="Retention (giorni)"
                      error={getFieldError('backup.retentionDays')}
                    >
                      <input
                        type="number"
                        value={formState.backup?.retentionDays || ''}
                        onChange={(e) => updateField('backup.retentionDays', e.target.value ? Number(e.target.value) : undefined)}
                        placeholder="7"
                        min={1}
                        max={365}
                        className="input"
                      />
                    </FormField>

                    <FormField
                      label="Includi Uploads"
                      error={getFieldError('backup.includeUploads')}
                    >
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formState.backup?.includeUploads !== false}
                          onChange={(e) => updateField('backup.includeUploads', e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Includi directory uploads nel backup</span>
                      </label>
                    </FormField>

                    {includeUploads && (
                      <FormField
                        label="Comprimi Uploads"
                        error={getFieldError('backup.compressUploads')}
                      >
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formState.backup?.compressUploads !== false}
                            onChange={(e) => updateField('backup.compressUploads', e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">Comprimi uploads con gzip</span>
                        </label>
                      </FormField>
                    )}
                  </>
                )}
              </div>
            </AccordionSection>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 bg-gray-50">
            <button
              onClick={handleReset}
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Ripristina tutto
            </button>

            <div className="flex items-center gap-3">
              <button onClick={onClose} className="btn-secondary">
                Annulla
              </button>
              <button onClick={handleSubmit} className="btn-primary">
                <Package className="w-4 h-4 mr-2" />
                Genera Pacchetto
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Helper Components ────────────────────────────────────────

interface AccordionSectionProps {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function AccordionSection({ title, isExpanded, onToggle, children }: AccordionSectionProps) {
  return (
    <div className="border border-gray-200 rounded-lg mb-3">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-lg"
      >
        <span className="font-medium text-gray-900">{title}</span>
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500" />
        )}
      </button>
      {isExpanded && <div className="px-4 py-4">{children}</div>}
    </div>
  );
}

interface FormFieldProps {
  label: string;
  error?: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}

function FormField({ label, error, hint, className, children }: FormFieldProps) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      {children}
      {hint && !error && (
        <p className="mt-1 text-xs text-gray-500">{hint}</p>
      )}
      {error && (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      )}
    </div>
  );
}
